import { type ReactNode, useState, useEffect } from "react";
import "./LinedTabs.css";
import clsx from "clsx";

interface LinedTabsItem {
	key: string;
	label: string;
	iconSuffix?: string;
	iconSuffixOnClick?: () => void;
	hidden?: boolean;
	disabled?: boolean;
	badge?: ReactNode;
}

interface LinedTabsProps {
	items: LinedTabsItem[];
	active?: string | undefined;
	children?: ReactNode;
	uppercase?: boolean;
	onChange?: (key: string) => void;
}

function LinedTabs({ items, active, children, onChange, uppercase = true }: LinedTabsProps) {
	const [current, setCurrent] = useState<string | undefined>(undefined);

	useEffect(() => {
		setCurrent(active);
	}, [active]);

	const handleChange = (key: string) => {
		const item = items.find((i) => i.key === key);
		if (item?.disabled) {
			return;
		}

		setCurrent(key);

		if (onChange) {
			onChange(key);
		}
	};

	return (
		<div className="lined-tabs">
			<div>
				{items.map((item) => (
					<div
						key={item.key}
						role="tab"
						aria-selected={item.key === current}
						aria-disabled={item.disabled || undefined}
						className={clsx("lined-tabs__item", {
							"is-current": item.key === current && !item.disabled,
							"is-disabled": item.disabled,
							hidden: item?.hidden,
						})}
						onClick={() => handleChange(item.key)}
					>
						<span className="lined-tabs__item-head">
							<span className={clsx("lined-tabs__item-label", { uppercase })}>{item.label}</span>
							{item.badge != null && item.badge !== false ? <span className="lined-tabs__item-badge">{item.badge}</span> : null}
						</span>
						{!item.disabled && item.iconSuffix ? (
							<span
								className={`icon icon-new icon-${item.iconSuffix}`}
								onClick={(e) => {
									e.stopPropagation();
									item.iconSuffixOnClick?.();
								}}
							/>
						) : null}
					</div>
				))}
			</div>
			<div className="lined-tabs__aside">{children}</div>
		</div>
	);
}

export { LinedTabs, type LinedTabsItem, type LinedTabsProps };
